import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n';
import { 
  MapPin, 
  Bell, 
  Users, 
  PenSquare,
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles
} from 'lucide-react';

const COMMUNES = [
  "Saint-Denis", "Saint-Paul", "Saint-Pierre", "Saint-Louis", "Saint-André",
  "Saint-Benoît", "Saint-Joseph", "Saint-Leu", "Le Tampon", "Le Port",
  "La Possession", "Sainte-Marie", "Sainte-Suzanne", "Petite-Île", "Bras-Panon",
  "La Plaine des Palmistes", "Entre-Deux", "Cilaos", "Salazie", "Les Trois-Bassins",
  "Les Avirons", "L'Étang-Salé", "Saint-Philippe", "Sainte-Rose"
];

const OnboardingModal = ({ onComplete }) => {
  const navigate = useNavigate();
  const { user, api, refreshUser } = useAuth();
  const { language } = useLanguage();
  const [step, setStep] = useState(0);
  const [selectedCommune, setSelectedCommune] = useState(user?.commune || '');
  const [loading, setLoading] = useState(false);

  const steps = [
    {
      id: 'welcome',
      title: language === 'cr' ? 'Byinvni si Bourbon!' : 'Bienvenue sur Bourbon !',
      subtitle: language === 'cr' 
        ? 'Lo rézo sosyal péi pou La Rényon'
        : 'Le réseau social local pour La Réunion',
      icon: Sparkles,
      color: 'text-primary'
    },
    {
      id: 'commune',
      title: language === 'cr' ? 'Kot ou rèst ?' : 'Où habitez-vous ?',
      subtitle: language === 'cr'
        ? 'Shwazi out kominn pou vwar sak i pass pré d\'ou'
        : 'Choisissez votre commune pour voir ce qui se passe près de chez vous',
      icon: MapPin,
      color: 'text-blue-500'
    },
    {
      id: 'features',
      title: language === 'cr' ? 'Sak ou pé fèr' : 'Ce que vous pouvez faire',
      subtitle: language === 'cr'
        ? 'Dékouvèr tout bann fonksyonalité'
        : 'Découvrez toutes les fonctionnalités',
      icon: Users,
      color: 'text-purple-500'
    },
    {
      id: 'ready',
      title: language === 'cr' ? 'Ou lé paré !' : 'Vous êtes prêt !',
      subtitle: language === 'cr'
        ? 'Komans partaz èk out kominité'
        : 'Commencez à partager avec votre communauté',
      icon: Check,
      color: 'text-green-500'
    }
  ];

  const features = [
    {
      icon: Bell,
      title: language === 'cr' ? 'Alèrt' : 'Alertes',
      desc: language === 'cr' ? 'Koupir dolo, bouchon, aksidan' : 'Coupures d\'eau, embouteillages, incidents'
    },
    {
      icon: Sparkles,
      title: language === 'cr' ? 'Évènman' : 'Événements',
      desc: language === 'cr' ? 'Konsèr, marshé, fèt kartié' : 'Concerts, marchés, fêtes de quartier'
    },
    {
      icon: Users,
      title: language === 'cr' ? 'Kominité' : 'Communauté',
      desc: language === 'cr' ? 'Koz èk out vwazin' : 'Échangez avec vos voisins'
    },
    {
      icon: PenSquare,
      title: language === 'cr' ? 'Partaz' : 'Partage',
      desc: language === 'cr' ? 'Publyé bann post èk foto' : 'Publiez des posts avec photos'
    }
  ];

  const handleNext = async () => {
    if (step === 1 && selectedCommune) {
      // Save commune selection
      setLoading(true);
      try {
        await api.put('/users/me', { commune: selectedCommune });
        await refreshUser?.();
      } catch (error) {
        console.error('Error updating commune:', error);
      }
      setLoading(false);
    }
    
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      // Complete onboarding
      await completeOnboarding();
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const completeOnboarding = async () => {
    setLoading(true);
    try {
      await api.put('/users/me', { onboarding_completed: true });
      await refreshUser?.();
      onComplete?.();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      onComplete?.();
    }
    setLoading(false);
  };

  const skipOnboarding = async () => {
    await completeOnboarding();
  };

  const currentStep = steps[step];
  const StepIcon = currentStep.icon;
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
      <Card className="w-full max-w-md overflow-hidden">
        {/* Progress */}
        <div className="h-1 bg-muted">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        <CardContent className="p-6">
          {/* Step Indicator */}
          <div className="flex justify-center mb-6">
            <div className={`p-4 rounded-full bg-muted ${currentStep.color}`}>
              <StepIcon className="h-8 w-8" />
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">{currentStep.title}</h2>
            <p className="text-muted-foreground">{currentStep.subtitle}</p>
          </div>

          {/* Step Content */}
          <div className="min-h-[200px] flex flex-col justify-center">
            {step === 0 && (
              <div className="text-center space-y-4">
                <p className="text-lg">
                  {language === 'cr' 
                    ? '🏝️ Bourbon i konèkt aou èk tout sak i pass dann out kartié.'
                    : '🏝️ Bourbon vous connecte à tout ce qui se passe dans votre quartier.'}
                </p>
                <div className="flex justify-center gap-2 text-2xl">
                  <span>🚨</span>
                  <span>🎉</span>
                  <span>💰</span>
                  <span>ℹ️</span>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <Select value={selectedCommune} onValueChange={setSelectedCommune}>
                  <SelectTrigger className="w-full" data-testid="onboarding-commune-select">
                    <SelectValue placeholder={language === 'cr' ? 'Shwazi out kominn' : 'Sélectionnez votre commune'} />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMUNES.map((commune) => (
                      <SelectItem key={commune} value={commune.toLowerCase().replace(/ /g, '-')}>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {commune}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground text-center">
                  {language === 'cr'
                    ? 'Ou pé shanzé sa pli tar dann paramèt'
                    : 'Vous pourrez changer cela plus tard dans les paramètres'}
                </p>
              </div>
            )}

            {step === 2 && (
              <div className="grid grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="p-3 bg-muted/50 rounded-lg text-center"
                  >
                    <feature.icon className="h-6 w-6 mx-auto mb-2 text-primary" />
                    <p className="font-medium text-sm">{feature.title}</p>
                    <p className="text-xs text-muted-foreground">{feature.desc}</p>
                  </div>
                ))}
              </div>
            )}

            {step === 3 && (
              <div className="text-center space-y-4">
                <div className="text-6xl">🎉</div>
                <p className="text-lg">
                  {language === 'cr'
                    ? 'Ou lé paré pou dékouvèr sak i pass dann out kartié !'
                    : 'Vous êtes prêt à découvrir ce qui se passe dans votre quartier !'}
                </p>
                <Button 
                  onClick={() => navigate('/create')}
                  variant="outline"
                  className="mt-2"
                >
                  <PenSquare className="h-4 w-4 mr-2" />
                  {language === 'cr' ? 'Kré mon promyé post' : 'Créer mon premier post'}
                </Button>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-6 pt-4 border-t">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={step === 0}
              className={step === 0 ? 'invisible' : ''}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {language === 'cr' ? 'Avan' : 'Précédent'}
            </Button>

            <div className="flex gap-1">
              {steps.map((_, index) => (
                <div 
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === step ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>

            <Button onClick={handleNext} disabled={loading || (step === 1 && !selectedCommune)}>
              {step === steps.length - 1 
                ? (language === 'cr' ? 'Komansé' : 'Commencer')
                : (language === 'cr' ? 'Suivant' : 'Suivant')}
              {step < steps.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>

          {/* Skip */}
          {step < steps.length - 1 && (
            <button
              onClick={skipOnboarding}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground mt-4"
            >
              {language === 'cr' ? 'Pasé' : 'Passer'}
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingModal;
